import React from 'react';
import { Monitor, Smartphone, Database, Globe, Zap, Puzzle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useLanguage } from '../i18n';

export const Services = () => {
  const { copy } = useLanguage();
  const serviceMeta = [
    {
      icon: Smartphone,
      featureIcons: [Globe, Database, Zap, Monitor],
      image: `${process.env.PUBLIC_URL}/images/mobile-applications.png`
    },
    {
      icon: Monitor,
      featureIcons: [Zap, Zap, Zap, Puzzle, Globe],
      image: `${process.env.PUBLIC_URL}/images/service-1.jpg`
    },
    {
      icon: Database,
      featureIcons: [Database, Zap, Globe, Monitor],
      image: `${process.env.PUBLIC_URL}/images/service-3.jpg`
    }
  ];
  const services = copy.services.items.map((service, index) => ({
    ...serviceMeta[index],
    ...service,
    features: service.features.map((text, featureIndex) => ({
      icon: serviceMeta[index].featureIcons[featureIndex],
      text,
    })),
    types: service.types?.map(([name, desc]) => ({ name, desc })),
  }));

  return (
    <section id="services" className="py-24 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 space-y-4">
          <span className="px-4 py-2 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm font-medium inline-block">
            {copy.services.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold">
            {copy.services.title}<span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">{copy.services.accent}</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {copy.services.intro}
          </p>
        </div>

        <Tabs defaultValue="0" className="w-full">
          <TabsList className="grid w-full grid-cols-1 md:grid-cols-3 mb-12 h-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <TabsTrigger
                  key={index}
                  value={String(index)}
                  className="flex items-center gap-2 py-4 data-[state=active]:bg-orange-100 dark:data-[state=active]:bg-orange-900/30 data-[state=active]:text-orange-600 dark:data-[state=active]:text-orange-400"
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-semibold">{service.title}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {services.map((service, index) => (
            <TabsContent key={index} value={String(index)} className="space-y-8">
              <div className="grid lg:grid-cols-5 gap-12 items-start">
                <div className="lg:col-span-3 space-y-6">
                  <h3 className="text-3xl font-bold">{service.title}</h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">{service.description}</p>
                  
                  <div className="space-y-4">
                    {service.features.map((feature, fIndex) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div key={fIndex} className="flex items-start gap-3 group">
                          <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 group-hover:scale-110 transition-transform">
                            <FeatureIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                          </div>
                          <p className="text-muted-foreground leading-relaxed pt-1">{feature.text}</p>
                        </div>
                      );
                    })}
                  </div>

                  {service.types && (
                    <div className="space-y-3 pt-4">
                      <h4 className="font-semibold text-lg">{copy.services.mobileTypes}</h4>
                      {service.types.map((type, tIndex) => (
                        <Card key={tIndex} className="border-l-4 border-l-orange-500">
                          <CardContent className="p-4">
                            <p className="font-semibold text-orange-600 dark:text-orange-400">{type.name}</p>
                            <p className="text-sm text-muted-foreground mt-1">{type.desc}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>

                <div className="lg:col-span-2 relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                  />
                  <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gradient-to-br from-orange-400 to-red-600 rounded-2xl -z-10 blur-2xl opacity-20"></div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};
